import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Editor } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import { useEditorState } from "@tiptap/react";
import {
	AlignCenter,
	AtSign,
	Bold,
	ChevronDown,
	Heading2,
	Heading3,
	Heading4,
	Image,
	Italic,
	List,
	ListOrdered,
	PenLine,
	Pilcrow,
	Quote,
	Redo2,
	Repeat2,
	SeparatorHorizontal,
	Sparkles,
	Table,
	Underline,
	Undo2,
} from "lucide-react";

import type { DocumentKind } from "@/api/db/connection";
import { cn } from "@/lib/utils";

import type { EditorField } from "./fields-context";
import { DocumentPatternSelection } from "./pattern-selection";
import { StepTitle } from "./step-view";

const CARD_VARIANTS = [
	{ value: "welcome", label: "Boas-vindas" },
	{ value: "info", label: "Informativo" },
	{ value: "alert", label: "Alerta" },
] as const;

interface DocumentToolbarProps {
	editor: Editor;
	fields: EditorField[];
	kind: DocumentKind;
	onInsertImage?: () => void;
	onEditField?: (field: EditorField) => void;
	onCreatePattern?: () => void;
	onEditPattern?: (patternId: number) => void;
}

export function DocumentToolbar({
	editor,
	fields,
	kind,
	onInsertImage,
	onEditField,
	onCreatePattern,
	onEditPattern,
}: DocumentToolbarProps) {
	const active = useEditorState({
		editor,
		selector: (ctx) => ({
			h2: ctx.editor.isActive("heading", { level: 2 }),
			h3: ctx.editor.isActive("heading", { level: 3 }),
			h4: ctx.editor.isActive("heading", { level: 4 }),
			paragraph: ctx.editor.isActive("paragraph"),
			bold: ctx.editor.isActive("bold"),
			underline: ctx.editor.isActive("underline"),
			italic: ctx.editor.isActive("italic"),
			center:
				ctx.editor.isActive("heading", { align: "center" }) ||
				ctx.editor.isActive("paragraph", { align: "center" }),
			listItem: ctx.editor.isActive("listItem"),
			step: ctx.editor.isActive("step"),
			paraphrase: ctx.editor.isActive("paragraph", { emphasis: "paraphrase" }),
			inspirational: ctx.editor.isActive("paragraph", { emphasis: "inspirational" }),
			card: ctx.editor.isActive("variantCard"),
			canUndo: ctx.editor.can().undo(),
			canRedo: ctx.editor.can().redo(),
			patternId: DocumentPatternSelection.id(ctx.editor),
			canCreatePattern:
				DocumentPatternSelection.id(ctx.editor) === null &&
				DocumentPatternSelection.range(ctx.editor) !== null,
		}),
	});

	const setHeading = (level: 2 | 3 | 4) => {
		editor.chain().focus().setNode("heading", { level }).run();
	};

	const toggleCenter = () => {
		const align = active.center ? null : "center";

		editor
			.chain()
			.focus()
			.updateAttributes("heading", { align })
			.updateAttributes("paragraph", { align })
			.run();
	};

	const setEmphasis = (value: "paraphrase" | "inspirational", current: boolean) => {
		editor
			.chain()
			.focus()
			.setNode("paragraph", { emphasis: current ? null : value })
			.run();
	};

	const setCard = (variant: string) => {
		if (active.card) {
			editor.chain().focus().updateAttributes("variantCard", { variant }).run();

			return;
		}

		editor.chain().focus().wrapIn("variantCard", { variant }).run();
	};

	const removeCard = () => {
		const { selection, tr } = editor.state;

		if (selection instanceof NodeSelection && selection.node.type.name === "variantCard") {
			editor.view.dispatch(tr.replaceWith(selection.from, selection.to, selection.node.content));
			editor.commands.focus();

			return;
		}

		const cursor = selection.$from;

		for (let level = cursor.depth; level > 0; level -= 1) {
			if (cursor.node(level).type.name !== "variantCard") {
				continue;
			}

			const card = cursor.node(level);
			const before = cursor.before(level);

			editor.view.dispatch(tr.replaceWith(before, before + card.nodeSize, card.content));
			editor.commands.focus();

			return;
		}
	};

	const setStep = () => {
		editor.chain().focus().setNode("step", { title: "" }).run();

		const { $from } = editor.state.selection;

		for (let level = $from.depth; level > 0; level -= 1) {
			if ($from.node(level).type.name !== "step") {
				continue;
			}

			const pos = $from.before(level);

			requestAnimationFrame(() => StepTitle.focus(editor, pos));

			return;
		}
	};

	const blockInsertPos = () => {
		const { $to } = editor.state.selection;

		return $to.depth === 0 ? $to.pos : $to.after(1);
	};

	const insertTable = () => {
		const row = () => ({
			type: "tableRow",
			content: [{ type: "tableCell" }, { type: "tableCell" }],
		});
		const table = { type: "table", content: [row(), row()] };
		const { $from } = editor.state.selection;
		const emptyTopParagraph =
			$from.depth === 1 &&
			$from.parent.type.name === "paragraph" &&
			$from.parent.content.size === 0;
		const pos = emptyTopParagraph ? $from.before(1) : blockInsertPos();
		const chain = editor.chain().focus();

		if (emptyTopParagraph) {
			chain.insertContentAt({ from: pos, to: $from.after(1) }, table);
		} else {
			chain.insertContentAt(pos, table);
		}

		chain.setTextSelection(pos + 3).run();
	};

	const insertPageBreak = () => {
		const pos = blockInsertPos();
		const atEnd = pos === editor.state.doc.content.size;
		const content = atEnd
			? [{ type: "pageBreak" }, { type: "paragraph" }]
			: [{ type: "pageBreak" }];

		editor.chain().focus().insertContentAt(pos, content).run();
	};

	const insertSignature = () => {
		editor
			.chain()
			.focus()
			.insertContentAt(blockInsertPos(), {
				type: "signature",
				content: [{ type: "signatureRow", content: [{ type: "signatureSlot" }] }],
			})
			.run();
	};

	const editPattern = () => {
		if (active.patternId === null || onEditPattern === undefined) {
			return;
		}

		onEditPattern(active.patternId);
	};

	return (
		<div
			data-component="document-toolbar"
			className="flex flex-wrap items-center gap-0.5 border-t border-border px-4 py-1.5"
		>
			<ToolbarButton
				label="Desfazer"
				disabled={!active.canUndo}
				onClick={() => editor.chain().focus().undo().run()}
			>
				<Undo2 />
			</ToolbarButton>

			<ToolbarButton
				label="Refazer"
				disabled={!active.canRedo}
				onClick={() => editor.chain().focus().redo().run()}
			>
				<Redo2 />
			</ToolbarButton>

			<ToolbarSeparator />

			<ToolbarButton
				label="Negrito"
				active={active.bold}
				onClick={() => editor.chain().focus().toggleMark("bold").run()}
			>
				<Bold />
			</ToolbarButton>

			<ToolbarButton
				label="Sublinhado"
				active={active.underline}
				onClick={() => editor.chain().focus().toggleMark("underline").run()}
			>
				<Underline />
			</ToolbarButton>

			<ToolbarButton
				label="Itálico"
				active={active.italic}
				onClick={() => editor.chain().focus().toggleMark("italic").run()}
			>
				<Italic />
			</ToolbarButton>

			<ToolbarButton label="Centralizar" active={active.center} onClick={toggleCenter}>
				<AlignCenter />
			</ToolbarButton>

			<ToolbarSeparator />

			<ToolbarGroupLabel>Transformar</ToolbarGroupLabel>

			<ToolbarButton
				label="Parágrafo"
				active={active.paragraph && !active.paraphrase && !active.inspirational}
				onClick={() => editor.chain().focus().setNode("paragraph", { emphasis: null }).run()}
			>
				<Pilcrow />
			</ToolbarButton>

			<ToolbarButton label="Título (H2)" active={active.h2} onClick={() => setHeading(2)}>
				<Heading2 />
			</ToolbarButton>

			<ToolbarButton label="Subtítulo (H3)" active={active.h3} onClick={() => setHeading(3)}>
				<Heading3 />
			</ToolbarButton>

			<ToolbarButton label="Subtítulo (H4)" active={active.h4} onClick={() => setHeading(4)}>
				<Heading4 />
			</ToolbarButton>

			<ToolbarButton
				label="Paráfrase"
				active={active.paraphrase}
				onClick={() => setEmphasis("paraphrase", active.paraphrase)}
			>
				<Quote />
			</ToolbarButton>

			<ToolbarButton
				label="Frase inspiracional"
				active={active.inspirational}
				onClick={() => setEmphasis("inspirational", active.inspirational)}
			>
				<Sparkles />
			</ToolbarButton>

			<ToolbarButton
				label="Item de lista"
				active={active.listItem}
				onClick={() => editor.chain().focus().setNode("listItem").run()}
			>
				<List />
			</ToolbarButton>

			<ToolbarButton label="Passo numerado" active={active.step} onClick={setStep}>
				<ListOrdered />
			</ToolbarButton>

			{kind === "term" && onCreatePattern !== undefined && (
				<>
					{active.patternId === null && (
						<ToolbarButton
							label="Criar padrão"
							disabled={!active.canCreatePattern}
							onClick={onCreatePattern}
						>
							<Repeat2 />
						</ToolbarButton>
					)}

					{active.patternId !== null && onEditPattern !== undefined && (
						<ToolbarButton label="Editar padrão" active onClick={editPattern}>
							<Repeat2 />
						</ToolbarButton>
					)}
				</>
			)}

			<ToolbarMenu label="Card" active={active.card}>
				{(close) => (
					<>
						{CARD_VARIANTS.map((variant) => (
							<MenuItem
								key={variant.value}
								onClick={() => {
									setCard(variant.value);
									close();
								}}
							>
								{variant.label}
							</MenuItem>
						))}

						{active.card && (
							<MenuItem
								onClick={() => {
									removeCard();
									close();
								}}
							>
								Remover card
							</MenuItem>
						)}
					</>
				)}
			</ToolbarMenu>

			<ToolbarSeparator />

			<ToolbarGroupLabel>Inserir</ToolbarGroupLabel>

			<ToolbarButton label="Tabela" onClick={insertTable}>
				<Table />
			</ToolbarButton>

			{kind === "manual" && (
				<ToolbarButton label="Quebra de página" onClick={insertPageBreak}>
					<SeparatorHorizontal />
				</ToolbarButton>
			)}

			<ToolbarButton label="Assinaturas" onClick={insertSignature}>
				<PenLine />
			</ToolbarButton>

			{onInsertImage !== undefined && (
				<ToolbarButton label="Imagem" onClick={onInsertImage}>
					<Image />
				</ToolbarButton>
			)}

			<ToolbarMenu label="Campo" icon={<AtSign />}>
				{(close) => (
					<>
						{fields.length === 0 && (
							<p className="px-2 py-1.5 text-xs text-muted-foreground">Nenhum campo cadastrado</p>
						)}

						{fields
							.filter(
								(field) =>
									field.patternId === undefined ||
									field.patternId === null ||
									field.patternId === active.patternId,
							)
							.map((field) => (
								<div key={field.id} className="flex items-center gap-1">
									<MenuItem
										onClick={() => {
											editor
												.chain()
												.focus()
												.insertContent({ type: "field", attrs: { fieldId: field.id } })
												.run();
											close();
										}}
										className="min-w-0 flex-1"
									>
										<span className="flex w-full items-baseline justify-between gap-3">
											<span className="truncate">{field.label}</span>
											{!!field.value && (
												<span className="max-w-36 truncate text-xs text-muted-foreground">
													{field.value}
												</span>
											)}
										</span>
									</MenuItem>

									{field.inputType !== undefined &&
										field.source === "input" &&
										onEditField !== undefined && (
											<button
												type="button"
												onMouseDown={(event) => event.preventDefault()}
												onClick={() => {
													onEditField(field);
													close();
												}}
												className="shrink-0 rounded-md px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
											>
												Editar
											</button>
										)}
								</div>
							))}
					</>
				)}
			</ToolbarMenu>
		</div>
	);
}

interface ToolbarButtonProps {
	label: string;
	active?: boolean;
	disabled?: boolean;
	onClick(): void;
	children: ReactNode;
}

function ToolbarButton({
	label,
	active = false,
	disabled = false,
	onClick,
	children,
}: ToolbarButtonProps) {
	return (
		<button
			type="button"
			title={label}
			aria-label={label}
			aria-pressed={active}
			disabled={disabled}
			onMouseDown={(event) => event.preventDefault()}
			onClick={onClick}
			className={cn(
				"flex size-8 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-4",
				active && "bg-primary/10 text-primary",
			)}
		>
			{children}
		</button>
	);
}

function ToolbarSeparator() {
	return <span className="mx-1 h-5 w-px bg-border" aria-hidden />;
}

function ToolbarGroupLabel({ children }: { children: ReactNode }) {
	return (
		<span className="select-none px-1 text-xs font-medium uppercase tracking-wider text-foreground/35">
			{children}
		</span>
	);
}

interface ToolbarMenuProps {
	label: string;
	icon?: ReactNode;
	active?: boolean;
	children(close: () => void): ReactNode;
}

function ToolbarMenu({ label, icon, active = false, children }: ToolbarMenuProps) {
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) {
			return;
		}

		const onPointerDown = (event: PointerEvent) => {
			if (root.current !== null && !root.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};

		document.addEventListener("pointerdown", onPointerDown);

		return () => document.removeEventListener("pointerdown", onPointerDown);
	}, [open]);

	return (
		<div ref={root} className="relative">
			<button
				type="button"
				onMouseDown={(event) => event.preventDefault()}
				onClick={() => setOpen((current) => !current)}
				className={cn(
					"flex h-8 items-center gap-1 rounded-md px-2 text-xs font-medium text-foreground/70 transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&_svg]:size-4",
					active && "bg-primary/10 text-primary",
				)}
			>
				{icon}
				{label}
				<ChevronDown className="size-3" />
			</button>

			{open && (
				<div className="absolute left-0 top-full z-40 mt-1 flex min-w-44 flex-col surface-panel p-1 shadow-card">
					{children(() => setOpen(false))}
				</div>
			)}
		</div>
	);
}

function MenuItem({
	onClick,
	children,
	className,
}: {
	onClick(): void;
	children: ReactNode;
	className?: string;
}) {
	return (
		<button
			type="button"
			onMouseDown={(event) => event.preventDefault()}
			onClick={onClick}
			className={cn(
				"rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
				className,
			)}
		>
			{children}
		</button>
	);
}
